/* eslint-disable */
import React, {useContext, useEffect, useMemo, useState} from 'react';
import './checkbox.css';
import {Form, useParams, useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Check, Tag, Sparkles, Gift, X, Receipt, Truck,
} from 'lucide-react';
import getOrganizationById from '../../helpers/getOrganization';
import DetailsHeader from '../../components/details-header/DetailsHeader';
import InfoCard from '../../components/cards/info-card';
import DataCard from '../../components/cards/data-card';
import PaymentOptions from '../../components/payment-options/payment-options';
import BatchModal from '../../components/batch-modal/BatchModal';
import MinDetailsHeader from '../../components/min-details-header/MinDetailsHeader';
import AddressDetails, {isSameAddress, toBlockLetterAddress} from '../../components/address/AddressDetails';
import {PaidSVG, PdfSVG, DownloadSVG} from '../../components/icons';
import InstalmentDetails from '../../components/instalment-details/instalmentDetails';
import InactiveOrderNotice from '../../components/inactive-order-notice/inactive-order-notice';
import {ToastContext} from '../../contexts/toast-context-provider';
import PaymentCard from '../../components/cards/payment-card';
import orderGateHook from '../../api/orderGateHook';
import Spinner from '../../components/spinner/spinner';
import {PaymentContext} from '../../contexts/payment-context-provider';
import StateDetails from '../../components/address/StateDetails';
import useDeviceInfo from '../../helpers/deviceDetect';
import CouponModal from '../../components/coupon-modal/CouponModal';
import {onUserLogin, setAnalyticsUser, trackEvent} from '../../utils/analytics';
import EventNames from '../../utils/event-name-constants';
import TermsAcceptance from '../../components/terms/terms-acceptance';
import formatRupees from '../../utils/currency';

// Same artwork as the paid icon; kept under its own name for the balance tile.
const BalanceSVG = PaidSVG;

function OrderDetails() {
    const navigate = useNavigate();
    const {showToast} = useContext(ToastContext);
    const [paymentData, setPaymentData] = useContext(PaymentContext);
    const [orderBreakdown, setOrderBreakdown] = useState([]);
    const [orderDetail, setOrderDetail] = useState({
        order: {
            id: 'a',
            paymentStatus: '',
            costSplits: [],
            userInstalmentPlan: {
                userInstalments: [],
            },
            totalTax: 0.00,
            createdBy: null,
            totalPayable: 0,
            userId: null,
        },
        sku: {
            state: null,
            name: '',
        },
    });
    const [loadingR, errorR, orderR, applyCoupon] = orderGateHook.applyCoupon();
    const [coupons, loadingC, errorC] = orderGateHook.getCoupons({
        userId: orderDetail?.order?.userId,
        orgId: orderDetail?.order?.orgId,
        skuId: orderDetail?.sku?.skuId
    });

    const [orderStatus, setOrderStatus] = useState('');
    const [paymentMode, setPaymentMode] = useState('full');
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [userDetails, setUserDetails] = useState({
        address: {
            area: '',
            city: '',
            state: '',
            houseNo: '',
            pinCode: '',
            landmark: '',
        },
        // GST billing address - mandatory on every first payment, whatever the
        // product. `state` holds the stateCode (the select's value); the name is
        // resolved at submit.
        billingAddress: {
            area: '',
            city: '',
            state: '',
            houseNo: '',
            pinCode: '',
            landmark: '',
        },
    });
    // Shipping defaults to the billing address; unticking reveals its own form.
    const [sameAsBilling, setSameAsBilling] = useState(true);
    const [buyerState, setBuyerState] = useState('');
    const [paidVia, setPaidVia] = useState('');
    const {orderId, orgId} = useParams();
    const supportTicketHref = getOrganizationById({orgId})?.supportUrl || '';
    const [order, loading, error, setRefresh] = orderGateHook.useGetOrdersDetails({orderId, orgId});
    const [states] = orderGateHook.useGetStatesList({countryCode: 'IN'});
    const [isFirstPayment, setIsFirstPayment] = useState(false);
    const [billLoading, billError, makePreInvoice] = orderGateHook.useMakePreInvoice();
    const [visitCentreLoading, visitCentreError, visitCentre] = orderGateHook.useVisitCentre();
    const [isInvoiceModalOpen, setIsModalOpen] = useState(false);
    const {device, isWebView} = useDeviceInfo();
    const [requireShipping, setRequireShipping] = useState(false);
    const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
    // Which draft the batch sheet is settling: one waiting on its primary batch, or
    // one held over a batch conflict whose two batches have both closed.
    const [batchModalMode, setBatchModalMode] = useState('pending');
    const {
        batches, loadingOptions, submitting, loadBatchOptions, selectBatch, confirmConflictBatch,
    } = orderGateHook.useOrderBatchSelection();
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    // TOTAL PAYBLE
    const [totalPayable, setTotalPayable] = useState(0);
    const toggleModal = () => {
        setIsModalOpen((val) => !val);
    };

    // The terms element core-admin resolved for this order's delivery mode, or
    // null when there is none to resolve - an org that never authored it, a
    // core-admin that could not be reached. TermsAcceptance draws the static
    // terms link in that case, and the acceptance is filed either way, with the
    // server's meta recording which of the two the buyer actually read.
    const checkoutTnc = orderDetail?.tnc || null;

    // Set when the student enrolled on a different batch than this order was for:
    // the server holds the order as a draft until they pick one of the two.
    const batchConflict = orderDetail?.order?.orderMeta?.batchConflict || null;
    const conflictPreviousBatch = batchConflict?.previousBatch || null;
    const conflictRequestedBatch = batchConflict?.requestedBatch || null;
    const isConflictSameBatch = Boolean(conflictPreviousBatch?.batchId)
        && String(conflictPreviousBatch.batchId) === String(conflictRequestedBatch?.batchId);

    // Only one relaxation (coupon or admin special price) can ever be applied
    // to an order. orderPriceRelaxation is present pre-payment too (unlike
    // orderMeta.isSpecialPrice/order.discount/order.totalPayable, which are
    // only denormalized once the order is paid), so it's the reliable signal
    // for "does this order already have a relaxation" and the reliable source
    // for the pre-payment price.
    const orderPriceRelaxation = orderDetail?.order?.orderPriceRelaxation;
    const hasActiveRelaxation = Boolean(orderPriceRelaxation) && orderPriceRelaxation?.relaxationStatus !== 'rejected';

    // Kept to one array per load: a fresh slice on every render re-ran PaymentOptions'
    // plan reset on every render, and threw away the buyer's pick.
    // A deactivated plan is still returned with the SKU (sku-model's getInstalmentPlans
    // selects every plan of it), and the server refuses one at checkout
    // (SKU_INSTALMENT_PLAN_NOT_ACTIVE), so offering it is a dead end. Filtered before the
    // slice, or a deactivated plan takes one of the three places an active one should have.
    const instalmentPlans = useMemo(
        () => (orderDetail?.sku?.instalmentPlans || [])
            .filter((plan) => plan?.isActive)
            .slice(0, 3),
        [orderDetail?.sku?.instalmentPlans],
    );

    // A plan the centre set the order up on binds the buyer - the server refuses any
    // other way to pay it - so the checkout offers just that plan: a standard plan
    // the admin recorded on the order, or a custom plan persisted with it.
    const adminPlan = useMemo(() => {
        if (!isFirstPayment) return null;
        const adminPlanId = orderDetail?.order?.orderMeta?.instalmentPlanId;
        if (adminPlanId) {
            const plan = (orderDetail?.sku?.instalmentPlans || [])
                .find((skuPlan) => String(skuPlan.instalmentPlanId) === String(adminPlanId));
            return plan ? {instalmentPlanId: plan.instalmentPlanId, name: plan.name, instalments: plan.instalments} : null;
        }
        const customInstalments = orderDetail?.order?.userInstalmentPlan?.userInstalments;
        if (customInstalments?.length) {
            return {
                instalmentPlanId: '',
                name: 'Your instalment plan',
                instalments: customInstalments.map((instalment) => ({
                    amount: Number(instalment.totalPayable),
                    dueDate: instalment.dueDate,
                })),
            };
        }
        return null;
    }, [isFirstPayment, orderDetail]);

    useEffect(() => {
        if (!adminPlan) return;
        setPaymentMode('partial');
        setSelectedPlanId(adminPlan.instalmentPlanId);
    }, [adminPlan]);

    // What PaymentOptions may select from: only the admin's plan when there is one.
    // Memoised for the same reason as instalmentPlans.
    const offeredPlans = useMemo(() => {
        if (!adminPlan) return instalmentPlans;
        return (orderDetail?.sku?.instalmentPlans || [])
            .filter((plan) => plan.instalmentPlanId === adminPlan.instalmentPlanId);
    }, [adminPlan, instalmentPlans, orderDetail?.sku?.instalmentPlans]);

    const {PAYMENT_PAGE_OPENED} = EventNames;

    const addressShape = {
        houseNo: z.string().min(3, 'Must be at least 3 characters'),
        area: z.string().min(3, 'Must be at least 3 characters'),
        state: z.string().min(1, 'Required'), // Ensuring state is not empty
        pinCode: z.string().regex(/^\d{6}$/, 'Invalid pincode'),
        city: z.string().min(3, 'Must be at least 3 characters'),
        landmark: z.string().optional(), // Making it optional
    };
    const termsField = z.boolean().refine((val) => val === true, {
        message: 'Check the checkbox to continue',
    });
    // First payment: the GST billing address is always required; the shipping
    // fields (the original flat keys) only when the product ships AND the buyer
    // unticked "Same as billing address". Later instalments validate nothing
    // but the submit path already skips validation for those (see onSubmit).
    const schema = z.object({
        billing: z.object(addressShape),
        ...(requireShipping && !sameAsBilling ? addressShape : {}),
        terms: termsField,
    });
    const stateSchema = z.object({
        terms: termsField,
    });
    const {
        register,
        handleSubmit,
        setValue,
        trigger,
        formState: {errors},
    } = useForm({
        resolver: zodResolver(isFirstPayment ? schema : stateSchema),
        mode: 'onChange',
    });

    const handleDownload = async (url) => {
        // console.log(url, 'url');
        if (isWebView && device === 'android') {
            // eslint-disable-next-line no-undef
            Android.downloadInvoiceUrlNew(url);
            return;
        }
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = url;
        link.click();
    };

    async function submitForm(values) {
        setIsProcessing(true);
        let instalmentPlanId = null;
        let paymentType = paymentMode === 'partial' ? 'instalment' : 'normal';
        setPaymentData({paymentMethod: paymentMode});
        if (paymentMode === 'partial') {
            setPaymentData((data) => ({
                ...data,
                instalmentDetails:
                    orderDetail.order?.userInstalmentPlan?.userInstalments?.find((instalment) => instalment?.instalmentStatus === 'due' || instalment?.instalmentStatus === 'overdue'),
            }));
        }
        // console.log(paymentMode, 'paymentMode')
        if (paymentMode !== 'full') {
            instalmentPlanId = selectedPlanId;
            if (orderDetail?.order?.paymentStatus === 'partial') {
                // TODO in case of second instalment, give instalment id
                const dueInstalment = orderDetail?.order?.userInstalmentPlan?.userInstalments?.find((instalment) => instalment?.instalmentStatus === 'due' || instalment?.instalmentStatus === 'overdue');
                instalmentPlanId = dueInstalment?.instalmentId;
                paymentType = 'instalment';
                // console.log(instalmentPlanId, 'insafjdaflkadfa', dueInstalment);
            }
        }
        // The selects store the state CODE; send the name alongside so the
        // server (and the legacy invoice render, which matches by name) never
        // has to guess which spelling it got.
        const withStateNames = (address) => {
            if (!address) return address;
            const stateRow = (states || []).find((st) => st.stateCode === address.state || st.name === address.state);
            return {
                ...address,
                state: stateRow?.name || address.state,
                stateCode: stateRow?.stateCode || address.stateCode || address.state,
            };
        };
        // GST billing address: first payments only - a later instalment is never
        // asked again, and the server reuses the copy committed to the order.
        const billingPayload = isFirstPayment ? withStateNames(userDetails?.billingAddress) : undefined;
        // Shipping keeps its historical home at userMeta.address; "Same as
        // billing address" simply copies the billing block into it.
        const shippingPayload = requireShipping && isFirstPayment
            ? (sameAsBilling ? billingPayload : withStateNames(userDetails?.address))
            : userDetails?.address;
        const response = await makePreInvoice({
            orderId,
            paymentType,
            // The tick is only taken on the first payment - a later instalment is
            // billed off an order whose terms were already agreed - so only that
            // submission carries a consent to file.
            tncAccepted: isFirstPayment,
            tncElementName: checkoutTnc?.elementName || '',
            createdBy: orderDetail?.order?.createdBy,
            // TODO WHY IS STATE NULL
            sellerState: orderDetail?.sku?.state ?? 'DL',
            // TODO DO NOT SEND BUYER STATE IN SECOND INSTALMENT
            // TODO MAKE ANOTHER STRUCTURAL COMPONENT FOR STATE
            buyerState: orderDetail?.sku?.state ?? 'DL',
            discount: orderDetail?.order?.discount,
            // paymentOption: paymentMode,
            userMeta: {address: shippingPayload},
            billingAddress: billingPayload,
            instalmentPlanId,
        });
        // console.log(response);
        if (response?.preInvoiceId) {
            let instalmentDetails = null;
            // console.log(paymentType, 'paymentMode')
            if (paymentType === 'instalment') {
                let instalmentNo = 1;
                if (!isFirstPayment) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const instalment in orderDetail?.userInstalmentPlan?.userInstalments) {
                        if (instalment?.instalmentStatus === 'paid') {
                            instalmentNo += 1;
                        }
                    }
                }
                instalmentDetails = {instalmentNo};
            }
            setPaymentData({paymentMethod: paymentType, instalmentDetails});
            // showToast('Bill has been successfully created');
            const paymentProperties = {
                price: orderDetail?.order?.totalPayable,
                productName: orderDetail?.order?.orderItems?.[0]?.productMeta?.name,
                productId: orderDetail?.order?.orderItems?.[0]?.productMeta?.id,
                batchId: orderDetail?.order?.orderItems?.[0]?.productMeta?.selectedBatches?.[0]?.batchId,
                batchName: orderDetail?.order?.orderItems?.[0]?.productMeta?.selectedBatches?.[0]?.name,
                exam: orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.exam?.name,
                session: orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.session?.name,
                stream: orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.stream?.name,
                center: orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.centre?.name,
                mode: orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.type,
                paymentMode: paymentMode === 'partial' ? 'instalment' : 'full',
                deviceType: device === 'desktop' ? 'web' : device,
                screenName: 'Proceed To Pay',
            };

            if (isWebView && device === 'android') {
                Android.paymentPageOpen(JSON.stringify(paymentProperties));
            } else if (isWebView && device === 'ios') {
                if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.callbackHandler) {
                    window.webkit.messageHandlers.callbackHandler.postMessage(paymentProperties);
                }
            } else {
                trackEvent(PAYMENT_PAGE_OPENED, paymentProperties);
            }
            navigate(`/${orgId}/bills/${response?.preInvoiceId}`); // Change this to your desired route
        } else {
            setIsProcessing(false);
            // Left open while the payment went through elsewhere: the toast has said the
            // order is paid, and the refetch brings the page up to date with it.
            // Replaced by a newer registration meanwhile: the refetch shows which.
            if (['ORDER_ALREADY_PAID', 'ORDER_SUPERSEDED'].includes(response?.errorCode)) {
                setRefresh((value) => value + 1);
            } else {
                console.error('Payment link not received.');
            }
        }
    }

    useEffect(() => {
        setOrderDetail(order);
    }, [loading]);

    // Offline courses: user opts to complete enrolment at the centre. Parks the
    // order as a draft on the server, then shows the visit-centre thank-you page.
    // Options are loaded when the sheet opens rather than on page load: most orders are
    // not waiting on a batch, and the endpoint is scoped to this order anyway.
    async function openBatchModal(mode) {
        setBatchModalMode(mode);
        setIsBatchModalOpen(true);
        await loadBatchOptions({ orderId });
    }

    // Confirming the batch confirms the order, so the page is refetched rather than
    // patched — paymentStatus, the batch fields and the payable amount all move.
    async function handleConfirmBatch(batchId) {
        const choose = batchModalMode === 'conflict' ? confirmConflictBatch : selectBatch;
        const done = await choose({ orderId, batchId });
        if (done) {
            setIsBatchModalOpen(false);
            showToast('Your batch has been confirmed.');
            setRefresh((value) => value + 1);
        }
    }

    // Settling a batch conflict makes the order payable again, so the page is
    // refetched, as for a confirmed batch above.
    async function handleConfirmConflictBatch(batchId) {
        const done = await confirmConflictBatch({ orderId, batchId });
        if (done) {
            showToast('Your batch has been confirmed.');
            setRefresh((value) => value + 1);
        }
    }

    async function handleVisitCentre() {
        // Require the terms & conditions checkbox, same as "Proceed To Pay".
        const termsAccepted = await trigger('terms');
        if (!termsAccepted) return;

        const result = await visitCentre({
            orderId,
            orgId,
            tncAccepted: true,
            tncElementName: checkoutTnc?.elementName || '',
        });
        if (result?.paymentStatus === 'draft') {
            navigate(`/${orgId}/orders/${orderId}/visit-centre-confirmation`);
        }
    }

    useEffect(() => {
        if (!orderDetail?.order?.userMeta?.email) return;
        setAnalyticsUser(orderDetail.order.userMeta);
        onUserLogin(orderDetail.order.userMeta);
    }, [orderDetail]);
    const [isProcessing, setIsProcessing] = useState(false);
    const handleDiscountBreakdown = (discountAmount) => {
        setOrderBreakdown((prev) => {
            const newBreakdown = [...prev];
            const discountIndex = newBreakdown.findIndex((item) => item.label === 'Discount');
            if (discountIndex !== -1) {
                newBreakdown[discountIndex].amount = discountAmount;
            } else {
                newBreakdown.push({label: 'Discount', amount: discountAmount});
            }
            return newBreakdown;
        });
    };

    useEffect(() => {
        setOrderStatus(String(orderDetail?.order?.paymentStatus)
            .charAt(0)
            .toUpperCase() + String(orderDetail?.order?.paymentStatus).slice(1));
        if (orderDetail?.order?.paymentStatus === 'init' || orderDetail?.order?.paymentStatus === 'open') {
            setIsFirstPayment(true);
        }
        if (orderDetail?.order?.paymentStatus === 'partial') {
            setPaymentMode('partial');
        }
        // Profile-sourced addresses carry the state NAME while the selects store
        // the CODE - resolve either spelling into the option list's code.
        const toStateCode = (value) => {
            if (!value) return '';
            const match = (states || []).find((st) => st.stateCode === value
                || String(st.name).toLowerCase() === String(value).toLowerCase());
            return match?.stateCode || '';
        };
        // The student's addresses on file (the account address registry) come
        // first: they are what the student last registered, and can be newer than
        // the copy the order took when it was written. The server sends them only
        // for an order awaiting its first payment; the order's own copies stand in
        // where nothing is on file.
        const addressOnFile = orderDetail?.addressOnFile;
        const seedShipping = addressOnFile?.deliveryAddress
            ?? orderDetail?.order?.userMeta?.address ?? addressOnFile?.billingAddress ?? undefined;
        // The Billing Address on file, else the billing address the order carries
        // (admin-created orders arrive with one), else the shipping/profile address
        // as a starting point.
        const seedBillingSource = addressOnFile?.billingAddress
            ?? orderDetail?.order?.userMeta?.billingAddress ?? seedShipping;
        // Block letters from the start: a prefilled value the buyer never touches
        // is submitted as-is, so it is converted here, not only as they type.
        const seedBilling = toBlockLetterAddress({
            houseNo: seedBillingSource?.houseNo || '',
            area: seedBillingSource?.area || '',
            city: seedBillingSource?.city || '',
            state: toStateCode(seedBillingSource?.stateCode || seedBillingSource?.state),
            pinCode: seedBillingSource?.pinCode || '',
            landmark: seedBillingSource?.landmark || '',
        });
        const normalizedShipping = seedShipping
            ? toBlockLetterAddress({...seedShipping, state: toStateCode(seedShipping?.stateCode || seedShipping?.state) || seedShipping?.state})
            : seedShipping;
        setUserDetails({
            address: normalizedShipping,
            billingAddress: seedBilling,
        });
        // The inputs render from userDetails, but react-hook-form validates its
        // own store - sync the prefill in so an untouched, prefilled form passes.
        Object.entries(seedBilling).forEach(([field, fieldValue]) => {
            setValue(`billing.${field}`, fieldValue);
        });
        if (normalizedShipping) {
            ['houseNo', 'area', 'city', 'state', 'pinCode', 'landmark'].forEach((field) => {
                setValue(field, normalizedShipping?.[field] || '');
            });
        }
        // Old key: requireShipping, new key requiresShipping
        setRequireShipping(orderDetail?.order?.orderMeta?.requiresShipping || orderDetail?.order?.orderMeta?.requireShipping);
        if (orderDetail?.sku) {
            setBuyerState(orderDetail?.sku?.state ?? 'DL');
        }
        if (orderDetail?.order?.bills?.[0]?.buyerState) {
            setBuyerState(orderDetail?.order?.bills?.[0].buyerState);
        }
        if (!(orderDetail?.order?.paymentStatus === 'partial' || orderDetail?.order?.paymentStatus === 'paid')) {
            if (orderDetail?.order?.appliedCoupon) {
                setAppliedCoupon({
                    code: orderDetail?.order?.appliedCoupon?.couponCode,
                    discount: orderDetail?.order?.appliedCoupon?.couponDetails?.discountType === 'percent' ? `${orderDetail?.order?.appliedCoupon?.couponDetails?.couponValue} %` : formatRupees(orderDetail?.order?.appliedCoupon?.couponDetails?.couponValue),

                });
            }
        }
        // `states` is a dependency because the address prefill resolves state
        // names into the codes the selects use, and the list loads async.
    }, [orderDetail, states]);

    // Delivery starts as the billing address - unless the student has a different
    // delivery address on file. That one starts unticked, so it is shown and kept
    // rather than replaced by the billing address when they pay. Not keyed on
    // `states`, so a late state list does not undo the buyer's own choice.
    useEffect(() => {
        const onFile = orderDetail?.addressOnFile;
        setSameAsBilling(!onFile?.deliveryAddress || isSameAddress(onFile.deliveryAddress, onFile.billingAddress));
    }, [orderDetail]);

    // The Order Breakdown follows the payment option selected. On a first payment
    // the server prices each option (costDetailsByPaymentOption): the order's own
    // figures can't serve where a plan's first bill repriced the order to that plan
    // before anything was paid. A later instalment, or a server that doesn't send
    // them yet, falls back to the order's own figures.
    useEffect(() => {
        const options = isFirstPayment ? orderDetail?.costDetailsByPaymentOption : null;
        const optionCostDetails = options
            && ((paymentMode === 'partial' && options.instalmentPlans?.[selectedPlanId]) || options.full);
        const costDetails = optionCostDetails || orderDetail?.order?.costDetails;
        // order.costSplits still reflects the pre-relaxation SKU cost splits
        // until payment succeeds - prefer costDetails.costSplits (derived live
        // from the active relaxation) pre-payment, same as totalTax/discount/
        // totalPayable below, so the base-amount line isn't blank/stale.
        const activeCostSplits = costDetails?.costSplits?.length
            ? costDetails.costSplits
            : orderDetail?.order?.costSplits;
        const breakdown = (activeCostSplits || []).map((cost) => ({
            label: cost?.labelInfo?.headLabel,
            amount: cost?.taxableAmount,
            priceHead: cost?.priceHead,
        }));
        breakdown.push({
            label: 'Total Tax(18%)',
            amount: costDetails?.totalTax ?? orderDetail?.order?.totalTax,
            priceHead: 'tax-fee',
        });
        // order.discount is only denormalized once the order is paid, so it
        // can't source the pre-payment discount line for either a coupon or
        // an admin special price - costDetails.discount reflects the active
        // relaxation directly, pre-payment too.
        const isSettled = orderDetail?.order?.paymentStatus === 'partial' || orderDetail?.order?.paymentStatus === 'paid';
        if (!isSettled && hasActiveRelaxation) {
            breakdown.push({label: 'Discount', amount: costDetails?.discount});
        }
        setOrderBreakdown(breakdown);
        setTotalPayable(costDetails?.totalPayable ?? orderDetail?.order?.totalPayable);
    }, [orderDetail, isFirstPayment, paymentMode, selectedPlanId]);

    const updateOrderBreakdown = ({
                                      updatedOrder,
                                      discountAmount,
                                  }) => {
        setOrderBreakdown([]);
        updatedOrder?.costSplits.forEach((cost) => {
            setOrderBreakdown((breakdown) => [...breakdown, {
                label: cost?.labelInfo?.headLabel,
                amount: cost?.taxableAmount,
                priceHead: cost?.priceHead,
            }]);
        });
        setOrderBreakdown((breakdown) => [...breakdown, {
            label: 'Total Tax(18%)',
            amount: updatedOrder?.totalTax,
            priceHead: 'tax-fee'
        }]);
        handleDiscountBreakdown(discountAmount);
    };

    const handleApplyCoupon = async (coupon) => {
        // CHANGE THE API SOURCE

        // console.log(coupon, 'APPLIED COUPON');
        await applyCoupon({
            couponCode: coupon?.code,
            orgId,
            accountId: orderDetail?.order?.userId,
            orderId: orderDetail?.order?.id,
        }).then((result) => {
            if (result === null || result?.success === false) {
                return;
            }
            // console.log(orderR, result, 'result');
            setAppliedCoupon(coupon);
            // Calculate discount based on coupon type

            // console.log('order Breakdown', orderBreakdown);
            setTotalPayable(result?.finalAmount);
            setRefresh((value) => value + 1);
            // Update order breakdown with discount
            handleDiscountBreakdown(result?.discountApplied);

            showToast('Coupon applied successfully!');
        }).catch((err) => {
            // showToast('Error in applying coupon !');
        });
    };

    // console.log(errorR, loadingR, orderR, 'responses');
    const [removeCouponLoading, removeCouponLoadingError, removeCoupon] = orderGateHook.removeCoupon();
    // console.log('payment mode', paymentMode);
    const handleRemoveCoupon = async () => {
        setAppliedCoupon(null);
        await removeCoupon({
            orderId: orderDetail?.order?.id,
            accountId: orderDetail?.order?.userId,
            couponCode: appliedCoupon?.code,
            orgId: orderDetail?.order?.orgId,
        }).then((result) => {
            if (result === null || result?.success === false) {
                return;
            }
            // Remove discount from order breakdown
            setTotalPayable(result?.finalAmount);
            setOrderBreakdown((prev) => prev.filter((item) => item.label !== 'Discount'));
            setRefresh((value) => value + 1);
            showToast('Coupon removed successfully!');
        });
    };

    return (
       <div className="bg-white md:bg-[#F7F8FA] pt-0 min-h-screen flex flex-col">

            {!loading && !error && (
                <Form
                    method="POST"
                    className="flex flex-col flex-grow"
                    onSubmit={isFirstPayment ? handleSubmit(submitForm) : (e) => {
                        e.preventDefault();
                        submitForm();
                    }}
                >

                    {!isFirstPayment && (
                        <DetailsHeader
                            skuName={orderDetail?.sku?.name}
                            batchDetails=""
                            paidVia={paidVia}
                            orderStatus={orderStatus}
                            orderDetails={{id: orderDetail?.order?.id}}
                            createdAt={orderDetail?.order?.createdAt}
                            transferMeta={orderDetail?.order?.orderMeta?.transferMeta}
                            orderItems={orderDetail?.order?.orderItems}
                        />
                    )}
                    {isFirstPayment
                        && (
                            <MinDetailsHeader
                                skuName={orderDetail?.sku?.name}
                                orderMessage=""
                                batchDetail={orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.exam?.name ?? orderDetail?.order?.orderItems?.[0]?.productMeta?.comboElements?.[0]?.course?.exam?.name}
                                courseType={orderDetail?.order?.orderItems?.[0]?.productMeta?.course?.type ?? orderDetail?.order?.orderItems?.[0]?.productMeta?.comboElements?.[0]?.course?.type}
                                hasTestPapers={Boolean(orderDetail?.order?.orderItems?.[0]?.productMeta?.testPapers)}
                                brand={orderDetail?.order?.orderItems?.[0]?.productMeta?.brand}
                                promotionalEventName={orderDetail?.order?.orderItems?.[0]?.productMeta?.promotionalEventName}
                                transferMeta={orderDetail?.order?.orderMeta?.transferMeta}
                                orderItems={orderDetail?.order?.orderItems}
                            />
                        )}
                    <div className="flex flex-grow items-center flex-col relative mt-4">
                        <div className="flex flex-col w-[90%] lg:w-4/5 ">
                            {!isFirstPayment && orderStatus !== 'Paid' && orderStatus !== 'Refunded'
                                && (
                                    <div className="flex mb-8 gap-5 justify-around w-full lg:w-3/4 xl:w-3/5">
                                        <DataCard
                                            icon={<PaidSVG/>}
                                            className="w-1/2"
                                            borderColor="#92D982"
                                            title="Amount Paid"
                                            value={orderDetail?.order?.amountPaid}
                                        />
                                        <DataCard
                                            icon={<BalanceSVG/>}
                                            className="w-1/2"
                                            borderColor="#FADD8D"
                                            title="Balance Amount"
                                            value={orderDetail?.order?.amountPending}
                                        />
                                    </div>
                                )}

                            {!isFirstPayment && (orderDetail?.order?.userInstalmentPlan?.userInstalments?.length > 0
                                ? <InstalmentDetails
                                    instalments={orderDetail?.order?.userInstalmentPlan?.userInstalments}/>
                                // TODO FIX TRANSACTION ID
                                : (
                                    <PaymentCard
                                        transactionId={orderDetail?.order?.id}
                                        paymentDoneOn={orderDetail?.order?.updatedAt}
                                        paymentStatus={orderDetail?.order?.paymentStatus}
                                        totalPayable={orderDetail?.order?.totalPayable}
                                    />
                                ))}
                            {isFirstPayment
                                && (
                                    <PaymentOptions
                                        selectedPlanId={selectedPlanId}
                                        setSelectedPlanId={setSelectedPlanId}
                                        paymentMode={paymentMode}
                                        setPaymentMode={setPaymentMode}
                                        instalmentPlans={offeredPlans}
                                        isSpecialPrice={orderDetail?.order?.orderMeta?.isSpecialPrice || hasActiveRelaxation}
                                        adminPlan={adminPlan}
                                        planStartDate={orderDetail?.instalmentStartDate}
                                    />
                                )}
                            {/* GST: the billing address is mandatory on every
                                first payment, whatever the product ships. */}
                            {isFirstPayment
                                && (
                                    <AddressDetails
                                        title="Billing Address"
                                        // null, not omitted: omitting it falls back to the delivery card's default line.
                                        subtitle={null}
                                        icon={Receipt}
                                        fieldPrefix="billing."
                                        addressKey="billingAddress"
                                        setValue={setValue}
                                        register={register}
                                        errors={errors}
                                        userDetails={userDetails}
                                        setUserDetails={setUserDetails}
                                        states={states}
                                    >
                                        {/* Only a product that ships asks for a delivery
                                            address; the toggle sits on the billing card so
                                            the default (same address) needs no second form. */}
                                        {requireShipping && (
                                            <label
                                                htmlFor="same-as-billing"
                                                className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                                            >
                                                <input
                                                    id="same-as-billing"
                                                    type="checkbox"
                                                    checked={sameAsBilling}
                                                    onChange={(e) => setSameAsBilling(e.target.checked)}
                                                    className="w-4 h-4 container"
                                                />
                                                <span>Delivery address is the same as billing address</span>
                                            </label>
                                        )}
                                    </AddressDetails>
                                )}
                            {isFirstPayment && requireShipping && !sameAsBilling
                                && (
                                    <AddressDetails
                                        subtitle="Where your study material will be delivered."
                                        icon={Truck}
                                        setValue={setValue}
                                        register={register}
                                        errors={errors}
                                        userDetails={userDetails}
                                        setUserDetails={setUserDetails}
                                        states={states}
                                    />
                                )}
                            {/*
              */}
                            {(Array.isArray(coupons?.coupon) && coupons?.coupon.length > 0) && paymentMode !== 'partial' && isFirstPayment && (!hasActiveRelaxation || appliedCoupon) && (
                                <div
                                    className="mb-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-[#B8E5FF] rounded-xl p-6 relative overflow-hidden"
                                >
                                    {/* Background decoration */}
                                    <div
                                        className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full -translate-y-16 translate-x-16"
                                    />

                                    <div className="relative">
                                        <div
                                            className={`${appliedCoupon ? 'mb-4 ' : ''}flex items-center justify-between gap-2`}>
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-blue-100 rounded-lg">
                                                    <Tag className="w-5 h-5 text-blue-600"/>
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-900">Coupons</h3>

                                            </div>
                                            {!appliedCoupon && (

                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        disabled={removeCouponLoading}
                                                        onClick={() => setIsCouponModalOpen(true)}
                                                        className={`${removeCouponLoading ? 'bg-gray-400' : 'bg-[#045692] hover:from-blue-700 hover:to-blue-800'
                                                        } text-white px-3 py-2 md:px-6 md:py-3 rounded-lg font-semibold text-xs md:text-base transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5`}
                                                    >
                                                        Apply Coupon Code
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {appliedCoupon && (
                                            /* Applied Coupon Display */
                                            <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-green-100 rounded-full">
                                                            <Gift className="w-5 h-5 text-green-600"/>
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-green-700 text-lg">
                                  {appliedCoupon.code}
                                </span>
                                                                {appliedCoupon.discount && (
                                                                    <span
                                                                        className="bg-green-100 text-green-700 px-2 py-1 rounded-full md:text-sm text-xs font-medium"
                                                                    >
                                    {appliedCoupon.discount}
                                  </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {/* <button */}
                                                        {/*  type="button" */}
                                                        {/*  onClick={() => setIsCouponModalOpen(true)} */}
                                                        {/*  className="px-4 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium" */}
                                                        {/* > */}
                                                        {/*  Change */}
                                                        {/* </button> */}
                                                        <button
                                                            type="button"
                                                            onClick={handleRemoveCoupon}
                                                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                                        >
                                                            <X className="w-4 h-4"/>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* <div className="text-center"> */}
                                        {/*  <button */}
                                        {/*    type="button" */}
                                        {/*    onClick={() => setIsCouponModalOpen(true)} */}
                                        {/*    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5" */}
                                        {/*  > */}
                                        {/*    Apply Coupon Code */}
                                        {/*  </button> */}
                                        {/* </div> */}

                                    </div>
                                </div>
                            )}
                            {/*
              */}
                            <InfoCard
                                title="Order Breakdown"
                                extra={(
                                    <div className="flex items-center gap-4">
                                        {/* {isFirstPayment && ( */}
                                        {/*  <button */}
                                        {/*    type="button" */}
                                        {/*    onClick={(e) => { */}
                                        {/*      e.preventDefault(); */}
                                        {/*      setIsCouponModalOpen(true); */}
                                        {/*    }} */}
                                        {/*    className="flex items-center justify-center bg-[#045692] text-white border border-[#B8E5FF] px-4 py-2 rounded-[6px] text-12px md:text-[16px] font-semibold hover:bg-[#1b84c2] " */}
                                        {/*  > */}
                                        {/*    {appliedCoupon ? 'Change Coupon' : 'Apply Coupon'} */}
                                        {/*  </button> */}
                                        {/* )} */}
                                        {orderDetail?.order?.invoices?.length > 0 && (
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    toggleModal();
                                                }}
                                                type="button"
                                                className="flex items-center text-[#334155] flex gap-1 text-12px md:text-[16px] font-medium cursor-pointer"
                                            >
                                                <DownloadSVG/>
                                                Invoice
                                            </button>
                                        )}
                                    </div>
                                )}
                                className="mb-8"
                            >
                                <div className="flex flex-col gap-2">
                                    {orderBreakdown.map((item, index) => (
                                        <div key={`a${index + 1}`}
                                             className="flex px-[9px] justify-between items-center">
                                            <span
                                                className="text-[14px] md:text-[18px] font-medium text-[#334155]">{item?.label}</span>
                                            {item.amount && (
                                                <span className="font-normal text-[14px] md:text-[18px] text-[#334155]">
                                                    {formatRupees(item.amount)}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    {appliedCoupon && (
                                        <div className="flex px-[9px] justify-between items-center text-green-600">
                      <span className="text-[14px] md:text-[18px] font-medium">
                        Applied Coupon:
                          {' '}
                          {appliedCoupon.code}
                      </span>
                                            {/* <button */}
                                            {/*  type="button" */}
                                            {/*  onClick={handleRemoveCoupon} */}
                                            {/*  className="ml-2 px-3 py-1 text-red-500 border border-red-500 rounded-[6px] text-[12px] md:text-[14px] font-medium hover:bg-red-50 transition-colors" */}
                                            {/* > */}
                                            {/*  Remove */}
                                            {/* </button> */}
                                        </div>
                                    )}
                                    <div
                                        className="flex justify-between items-center px-[10px] py-[6px] rounded-[6px] font-semibold text-[#334155] bg-[#D7F0FF] flex "
                                    >
                                        <span className="text-[14px] md:text-[18px] font-semibold">Final Amount</span>
                                        <span
                                            className="text-[14px] md:text-[18px] font-normal"
                                        >
                                            {formatRupees(totalPayable)}
                                        </span>
                                    </div>
                                </div>
                            </InfoCard>
                        </div>

                        {isFirstPayment
                            && (
                                <TermsAcceptance
                                    tnc={checkoutTnc}
                                    register={register}
                                    error={errors.terms?.message}
                                    fallbackHref={orderDetail?.order?.companyCode === 'MEL' ? 'https://www.nextias.com/payment-terms-and-conditions' : 'https://www.madeeasyprime.com/terms-condition'}
                                />
                            )}
                        {(orderStatus !== 'Paid' && orderStatus !== 'Refunded' && orderStatus !== 'Inactive' && orderStatus !== 'Draft') && (
                            <div
                                className="w-full flex flex-col md:flex-row items-center gap-3 py-5 justify-center px-[19px] bg-[#DFF3FF] border-t-[1px] border-t-[#81C4F5]"
                            >
                                <button
                                    type="submit"
                                    disabled={isProcessing}
                                    className={`w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] text-white rounded-[6px]
        ${isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#045692]'}`}
                                >
                                    {isProcessing ? 'Processing...' : 'Proceed To Pay'}
                                </button>
                                {(orderDetail?.canVisitCentre ?? orderDetail?.isOffline) && (
                                    <button
                                        type="button"
                                        disabled={visitCentreLoading || isProcessing}
                                        onClick={handleVisitCentre}
                                        className={`w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] rounded-[6px] border-[1px]
        ${visitCentreLoading ? 'bg-gray-200 cursor-not-allowed text-gray-500 border-gray-300' : 'bg-white text-[#045692] border-[#045692]'}`}
                                    >
                                        {visitCentreLoading ? 'Please wait...' : 'Visit centre'}
                                    </button>
                                )}
                            </div>
                        )}
                        {orderStatus === 'Draft' && batchConflict && (
                            <div
                                className="sticky bottom-0 z-10 mt-auto w-full flex flex-col items-center gap-3 py-5 px-[19px] bg-[#FFF8E1] border-t-[1px] border-t-[#FFC107]"
                            >
                                {isConflictSameBatch ? (
                                    <p className="text-[#8D6E63] font-semibold text-[15px] text-center">
                                        Please confirm your batch to continue.
                                    </p>
                                ) : (
                                    <p className="text-[#8D6E63] font-semibold text-[15px] text-center">
                                        You chose
                                        {' '}
                                        <span className="font-bold">{conflictRequestedBatch?.name || 'a different batch'}</span>
                                        {' '}
                                        on the course page, but this order is for
                                        {' '}
                                        <span className="font-bold">{conflictPreviousBatch?.name || 'another batch'}</span>
                                        . Confirm your batch to continue.
                                    </p>
                                )}
                                <div className="w-full flex flex-col md:flex-row items-center justify-center gap-3">
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        onClick={() => handleConfirmConflictBatch(conflictRequestedBatch?.batchId)}
                                        className={`w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] text-white rounded-[6px]
        ${submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#045692]'}`}
                                    >
                                        {submitting ? 'Please wait...' : `Continue with ${conflictRequestedBatch?.name || 'this batch'}`}
                                    </button>
                                    {!isConflictSameBatch && conflictPreviousBatch?.batchId && (
                                        <button
                                            type="button"
                                            disabled={submitting}
                                            onClick={() => handleConfirmConflictBatch(conflictPreviousBatch.batchId)}
                                            className={`w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] rounded-[6px] border-[1px]
        ${submitting ? 'bg-gray-200 cursor-not-allowed text-gray-500 border-gray-300' : 'bg-white text-[#045692] border-[#045692]'}`}
                                        >
                                            {`Keep ${conflictPreviousBatch.name || 'the earlier batch'}`}
                                        </button>
                                    )}
                                </div>
                                {/* Both batches can have closed or filled up since: the
                                    sheet lists what the course has open now. */}
                                <button
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => openBatchModal('conflict')}
                                    className="font-semibold text-[14px] text-[#045692] underline"
                                >
                                    Choose another batch
                                </button>
                            </div>
                        )}
                        {orderStatus === 'Draft' && orderDetail?.order?.isPrimaryBatchPending && (
                            <div
                                className="sticky bottom-0 z-10 mt-auto w-full flex flex-col items-center gap-3 py-5 px-[19px] bg-[#FFF8E1] border-t-[1px] border-t-[#FFC107]"
                            >
                                <p className="text-[#8D6E63] font-semibold text-[15px] text-center">
                                    Your order is on hold until you choose a batch.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => openBatchModal('pending')}
                                    className="w-full md:w-1/2 lg:w-1/3 py-4 font-bold text-[16px] text-white rounded-[6px] bg-[#045692]"
                                >
                                    Select your batch
                                </button>
                            </div>
                        )}
                        {/* A draft with nothing to do about it. Draft hides the pay
                            button, and the batch prompt above only appears when the
                            order is actually waiting on a batch — so without this the
                            page renders no control at all and the student is left
                            staring at an order they cannot pay for and cannot fix.
                            Whatever parked it (a centre visit, a combo the server
                            could not resolve a primary batch for, an admin), the
                            honest answer is the same: this needs a person. */}
                        {orderStatus === 'Draft' && !orderDetail?.order?.isPrimaryBatchPending && !batchConflict && (
                            <div
                                className="w-full flex flex-col items-center gap-2 py-5 px-[19px] bg-[#FFF8E1] border-t-[1px] border-t-[#FFC107]"
                            >
                                <p className="text-[#8D6E63] font-semibold text-[15px] text-center">
                                    Order for the course cannot be processed right now.
                                </p>
                                <p className="text-[#8D6E63] text-[14px] text-center">
                                    Please visit the centre or
                                    {' '}
                                    {supportTicketHref ? (
                                        <a
                                            href={supportTicketHref}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-semibold text-[#045692] underline"
                                        >
                                            raise a ticket
                                        </a>
                                    ) : 'raise a ticket'}
                                    .
                                </p>
                            </div>
                        )}
                        {orderStatus === 'Inactive' && (
                            <InactiveOrderNotice
                                orgId={orgId}
                                supersededBy={orderDetail?.order?.orderMeta?.supersededBy}
                            />
                        )}
                    </div>

                </Form>
            )}
            <BatchModal
                isOpen={isBatchModalOpen}
                batches={batches}
                loading={loadingOptions}
                submitting={submitting}
                onClose={() => setIsBatchModalOpen(false)}
                onConfirm={handleConfirmBatch}
            />
            {isInvoiceModalOpen && (
                <div className="fixed inset-0 flex items-end justify-center z-50">
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <div
                        className="bg-black bg-opacity-50 absolute inset-0 animate-fadeIn delay-100"
                        onClick={toggleModal}
                        tabIndex={0}
                        onKeyDown={() => {
                        }}
                        role="button"
                        aria-label="Close modal"
                    />

                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                    <div
                        className="bg-white rounded-t-2xl w-full md:max-w-[50%] max-w-[100%] p-4 relative z-10 animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={0}
                        onKeyDown={() => {
                        }}
                        role="button"
                    >
                        {/* Modal Handle */}
                        <div className="flex justify-center mb-4 ">
                            <div className="w-16 h-1 bg-gray-300 rounded-full"/>
                        </div>

                        {/* Invoice List */}
                        <div className="pb-6 max-h-[70vh] overflow-y-auto">
                            {(orderDetail?.order?.invoices || []).some((i) => i.pdfURL) ? orderDetail?.order?.invoices?.map((invoice, index) => (
                                <div key={invoice?.id} className="flex items-center justify-between py-3">
                                    <div className="flex items-center">
                                        <div className="p-2 rounded mr-3">
                                            <PdfSVG/>
                                        </div>
                                        <span className="font-medium">
                      Invoice
                                            {index + 1}
                    </span>
                                    </div>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={() => {
                                        }}
                                        onClick={() => {
                                            handleDownload(invoice.pdfURL);
                                        }}
                                        className="text-blue-600 p-2 rounded-full hover:bg-blue-50 transition"
                                    >
                                        <DownloadSVG/>
                                    </div>
                                </div>
                            )) : <div className="flex items-center justify-between py-3">No invoices available yet.
                                Please check back in an hour or contact support.</div>}
                        </div>
                    </div>
                </div>
            )}
            {loading && !error && <Spinner fullPage/>}
            {!loading && !error && (
                <CouponModal
                    orderCoupon={appliedCoupon}
                    coupons={coupons?.coupon}
                    isOpen={isCouponModalOpen}
                    onClose={() => setIsCouponModalOpen(false)}
                    onApplyCoupon={handleApplyCoupon}
                />
            )}
        </div>
    );
}

export default OrderDetails;
